import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch as fetchOne,
    fetchRecords,
    update,
} from '../services/DomainNamesService';

import {DomainName} from '../types';

// FNP-942: records are React Query's job. Selection, paging and keyword stay in
// Redux — see useDomainNamesSelection.ts and the visibility slice.
//
// The service layer is unchanged: these hooks call the same DomainNamesService
// functions the Redux thunks used to call, so the GraphQL surface and the
// backend's user-isolation filtering behave exactly as before.

export const domainNamesKeys = {
    all: ['domain-names'] as const,
    lists: () => [...domainNamesKeys.all, 'list'] as const,
    list: (filter: object) => [...domainNamesKeys.lists(), filter] as const,
    details: () => [...domainNamesKeys.all, 'detail'] as const,
    detail: (id: string) => [...domainNamesKeys.details(), id] as const,
};

export const useDomainNamesQuery = (filter: object = {}) => {
    return useQuery({
        queryKey: domainNamesKeys.list(filter),
        queryFn: async (): Promise<DomainName[]> => {
            const records = await fetchRecords(filter);
            return records ?? [];
        },
    });
};

export const useDomainNameQuery = (domainNameId?: string) => {
    return useQuery({
        queryKey: domainNamesKeys.detail(domainNameId ?? ''),
        queryFn: async () => {
            if (typeof domainNameId === 'undefined') {
                throw new Error('domainNameId is required parameter');
            }
            return await fetchOne(domainNameId);
        },
        // Without this the detail query fires with an empty id on the Add page,
        // where there is no record yet.
        enabled: typeof domainNameId !== 'undefined' && domainNameId !== '',
    });
};

export const useCreateDomainNameMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: DomainName) => add(values as unknown as Record<string, unknown>),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: domainNamesKeys.lists()});
        },
    });
};

export const useUpdateDomainNameMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: DomainName }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: domainNamesKeys.lists()});
            queryClient.invalidateQueries({queryKey: domainNamesKeys.detail(variables.id)});
        },
    });
};

export const useDeleteDomainNameMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: domainNamesKeys.lists()});
        },
    });
};
